import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import {
  UpdatePasswordFormBase,
  type UpdatePasswordFormBaseProps,
  type UpdatePasswordFormData,
} from "@core/features/profile";
import { AuthPasswordRequirementsContainer } from "@themes/pollinator/ui/AuthPasswordRequirements";

export type { UpdatePasswordFormData };

export type Props = Omit<
  UpdatePasswordFormBaseProps,
  "renderForm" | "renderActions"
>;

/**
 * Presentational component for the password update form.
 * Receives all data and callbacks via props.
 */
export function PasswordForm({
  form,
  onSubmit,
  onCancel,
  isSuccess,
  ...baseProps
}: Props) {
  return (
    <UpdatePasswordFormBase
      form={form}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSuccess={isSuccess}
      renderForm={({ form }: { form: UseFormReturn<UpdatePasswordFormData> }) => (
        <Form form={form} errorContainerClassName="p-0 w-full max-w-full mt-8">
          <FormField
            name="currentPassword"
            label="Current Password"
            component="Input"
            type="password"
            placeholder="Enter your current password"
            autoComplete="current-password"
            layout="horizontal"
            required
          />
          <FormField
            name="password"
            label="Password"
            component="Input"
            type="password"
            placeholder="Enter your new password"
            autoComplete="new-password"
            layout="horizontal"
            required
            isHideErrorMessage
          />
          <FormField
            name="confirmPassword"
            label="Confirm Password"
            component="Input"
            type="password"
            placeholder="Confirm your new password"
            autoComplete="new-password"
            layout="horizontal"
            required
          />
          <AuthPasswordRequirementsContainer layout="horizontal" />
        </Form>
      )}
      renderActions={({
        onSubmit,
        onCancel,
        isDirty,
        isValid,
        isSubmitting,
      }) => (
        <ActionButtonsGroup
          primaryButtonText="Update Password"
          onPrimaryClick={onSubmit}
          secondaryButtonText={isDirty ? "Cancel" : undefined}
          onSecondaryClick={isDirty ? onCancel : undefined}
          className="size-max w-fit self-end"
          primaryButtonProps={{
            disabled: !isDirty || isSubmitting,
            visuallyDisabled: isDirty && !isSubmitting && !isValid,
          }}
        />
      )}
      {...baseProps}
    />
  );
}

export default PasswordForm;
